import React, { useState, useEffect, useMemo } from 'react';
import { API } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2,
  RefreshCw,
  Save,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Info,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ToggleLeft,
  ToggleRight,
  Copy,
  Users,
  Wrench,
  Sparkles,
  Brain,
  Search,
  Download,
  Filter
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

const RulesPage = () => {
  const [rules, setRules] = useState([]);
  const [ebaDocuments, setEbaDocuments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);
  const [deletingSection, setDeletingSection] = useState(null);
  const [deletingEba, setDeletingEba] = useState(null);
  const [filterEba, setFilterEba] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRuleType, setFilterRuleType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [togglingSection, setTogglingSection] = useState(null);
  const navigate = useNavigate();
  
  // AI Suggestion state
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);

  // Check if the currently editing rule is an EBA-sourced rule
  const isEbaRule = editingRule && editingRule.eba_id && editingRule.eba_id !== 'default';

  const [formData, setFormData] = useState({
    eba_id: 'default',
    eba_type: 'default',
    category: '',
    conditions: {
      day_type: 'weekday',
      rate_type: '',
      value: '',
      unit: 'per hour',
      role_ids: [],
      equipment_type_ids: [],
      employee_types: []
    },
    outputs: {
      ordinary_hours: 8,
      overtime_1_5: 2,
      overtime_2_0: 0,
      rate_value: '',
      rate_unit: 'per hour'
    },
    explanation: '',
    source_section: '',
    source_hint: ''
  });

  const fetchRules = async () => {
    try {
      const response = await API.get('/rules', { params: { active_only: false } });
      setRules(response.data);
    } catch (error) {
      toast.error('Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchEbaDocuments = async () => {
    try {
      const response = await API.get('/eba');
      setEbaDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch EBA documents');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await API.get('/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles');
    }
  };

  const fetchEquipmentTypes = async () => {
    try {
      const response = await API.get('/equipment-types');
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment types');
    }
  };

  const fetchEmployeeTypes = async () => {
    try {
      const response = await API.get('/employee-types');
      // Extract just the names for the multi-select
      setEmployeeTypes(response.data.map(t => t.name));
    } catch (error) {
      setEmployeeTypes(['Operator', 'General Staff', 'Office', 'Casual', 'Supervisor', 'Apprentice']);
    }
  };

  // Fetch AI suggestions for rule assignments
  const fetchAiSuggestions = async (ruleCategory, allowanceCode) => {
    if (!ruleCategory && !allowanceCode) {
      setAiSuggestions(null);
      return;
    }
    
    setLoadingAiSuggestions(true);
    try {
      const params = new URLSearchParams();
      if (ruleCategory) params.append('rule_category', ruleCategory);
      if (allowanceCode) params.append('allowance_code', allowanceCode);
      
      const response = await API.get(`/ai/rule-suggestions?${params}`);
      setAiSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch AI suggestions:', error);
      setAiSuggestions(null);
    } finally {
      setLoadingAiSuggestions(false);
    }
  };

  // Record rule pattern for AI learning
  const recordRulePattern = async (rule, action = 'save_rule') => {
    try {
      await API.post('/ai/rule-patterns', {
        rule_id: rule.id || rule.rule_id,
        rule_category: rule.category,
        allowance_code: rule.outputs?.allowance_code,
        rate_category: rule.rate_category,
        role_ids: rule.conditions?.role_ids || [],
        role_names: (rule.conditions?.role_ids || []).map(id => getRoleName(id)).filter(Boolean),
        equipment_type_ids: rule.conditions?.equipment_type_ids || [],
        equipment_type_names: (rule.conditions?.equipment_type_ids || []).map(id => getEquipmentTypeName(id)).filter(Boolean),
        employee_types: rule.conditions?.employee_types || [],
        action: action
      });
    } catch (error) {
      console.error('Failed to record rule pattern:', error);
    }
  };

  // Apply AI suggestion
  const applyAiSuggestion = (type, items) => {
    if (type === 'roles' && items.length > 0) {
      const newRoleIds = items.map(s => s.id).filter(id => id && !formData.conditions.role_ids.includes(id));
      if (newRoleIds.length > 0) {
        setFormData(prev => ({
          ...prev,
          conditions: {
            ...prev.conditions,
            role_ids: [...prev.conditions.role_ids, ...newRoleIds]
          }
        }));
        toast.success(`Added ${newRoleIds.length} suggested role(s)`);
      }
    } else if (type === 'equipment' && items.length > 0) {
      const newEqIds = items.map(s => s.id).filter(id => id && !formData.conditions.equipment_type_ids.includes(id));
      if (newEqIds.length > 0) {
        setFormData(prev => ({
          ...prev,
          conditions: {
            ...prev.conditions,
            equipment_type_ids: [...prev.conditions.equipment_type_ids, ...newEqIds]
          }
        }));
        toast.success(`Added ${newEqIds.length} suggested equipment type(s)`);
      }
    } else if (type === 'employee_types' && items.length > 0) {
      const newTypes = items.map(s => s.name).filter(name => !formData.conditions.employee_types.includes(name));
      if (newTypes.length > 0) {
        setFormData(prev => ({
          ...prev,
          conditions: {
            ...prev.conditions,
            employee_types: [...prev.conditions.employee_types, ...newTypes]
          }
        }));
        toast.success(`Added ${newTypes.length} suggested employee type(s)`);
      }
    }
  };

  useEffect(() => {
    fetchRules();
    fetchEbaDocuments();
    fetchRoles();
    fetchEquipmentTypes();
    fetchEmployeeTypes();
  }, []);

  // Fetch AI suggestions when sheet opens with a rule that has a category
  // Note: We intentionally only trigger on editingRule changes, not formData changes
  // to avoid excessive refetches as user types. formData is used as fallback only.
  useEffect(() => {
    if (isSheetOpen && (editingRule?.category || formData.category)) {
      const category = editingRule?.category || formData.category;
      const allowanceCode = editingRule?.outputs?.allowance_code || formData.outputs?.allowance_code;
      fetchAiSuggestions(category, allowanceCode);
    } else if (!isSheetOpen) {
      setAiSuggestions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSheetOpen, editingRule?.category, editingRule?.outputs?.allowance_code]);

  // Distinct rule types present in the current rule set (for the filter dropdown)
  const ruleTypeOptions = useMemo(() => {
    const types = new Set();
    rules.forEach(r => {
      const t = r.rule_type || r.source_section;
      if (t) types.add(t);
    });
    return Array.from(types).sort();
  }, [rules]);

  // Compute grouped rules structure
  const groupedRules = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filteredRules = rules.filter(rule => {
      if (filterEba !== 'all' && rule.eba_id !== filterEba) return false;
      if (filterRuleType !== 'all' && (rule.rule_type || rule.source_section) !== filterRuleType) return false;
      if (filterPriority !== 'all' && (rule.priority || '') !== filterPriority) return false;
      if (filterStatus === 'active' && !rule.is_active) return false;
      if (filterStatus === 'inactive' && rule.is_active) return false;
      if (q) {
        const hay = [
          rule.category, rule.condition_text, rule.action_text, rule.explanation,
          rule.rule_id, rule.rule_type, rule.source_section,
          rule.conditions?.rate_type, rule.conditions?.condition_text
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const grouped = {};
    
    filteredRules.forEach(rule => {
      // Show "User Defined" for manually created rules (eba_type is 'default' or empty)
      const ebaKey = (!rule.eba_type || rule.eba_type === 'default') ? 'User Defined' : rule.eba_type;
      if (!grouped[ebaKey]) {
        grouped[ebaKey] = {};
      }
      // Show "Custom Rules" for rules without a source section
      const sectionKey = rule.source_section || 'Custom Rules';
      if (!grouped[ebaKey][sectionKey]) {
        grouped[ebaKey][sectionKey] = [];
      }
      grouped[ebaKey][sectionKey].push(rule);
    });
    
    return grouped;
  }, [rules, filterEba, searchQuery, filterRuleType, filterPriority, filterStatus]);

  // Total rules matching the current filters (for the count + export)
  const filteredRulesCount = useMemo(
    () => Object.values(groupedRules).reduce((acc, sections) => acc + Object.values(sections).flat().length, 0),
    [groupedRules]
  );

  const handleExportRules = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterEba !== 'all') params.eba_id = filterEba;
      if (filterRuleType !== 'all') params.rule_type = filterRuleType;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const response = await API.get('/rules/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_rules_validation_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rules exported for client validation');
    } catch (error) {
      toast.error('Failed to export rules');
    } finally {
      setExporting(false);
    }
  };

  // Helper to get role name by ID
  const getRoleName = (roleId) => {
    if (!roleId) return null;
    const role = roles.find(r => r.id === roleId);
    return role?.name || null;
  };

  // Helper to get multiple role names
  const getRoleNames = (roleIds) => {
    if (!roleIds || roleIds.length === 0) return [];
    return roleIds.map(id => getRoleName(id)).filter(Boolean);
  };

  // Helper to get equipment type name by ID
  const getEquipmentTypeName = (equipId) => {
    if (!equipId) return null;
    const equip = equipmentTypes.find(e => e.id === equipId);
    return equip?.name || null;
  };

  // Helper to get multiple equipment type names
  const getEquipmentTypeNames = (equipIds) => {
    if (!equipIds || equipIds.length === 0) return [];
    return equipIds.map(id => getEquipmentTypeName(id)).filter(Boolean);
  };

  // Helper to toggle role in selection
  const toggleRoleSelection = (roleId) => {
    const currentIds = formData.conditions.role_ids || [];
    const newIds = currentIds.includes(roleId)
      ? currentIds.filter(id => id !== roleId)
      : [...currentIds, roleId];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, role_ids: newIds }
    });
  };

  // Helper to toggle equipment in selection
  const toggleEquipmentSelection = (equipId) => {
    const currentIds = formData.conditions.equipment_type_ids || [];
    const newIds = currentIds.includes(equipId)
      ? currentIds.filter(id => id !== equipId)
      : [...currentIds, equipId];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, equipment_type_ids: newIds }
    });
  };

  // Helper to toggle employee type in selection
  const toggleEmployeeTypeSelection = (empType) => {
    const currentTypes = formData.conditions.employee_types || [];
    const newTypes = currentTypes.includes(empType)
      ? currentTypes.filter(t => t !== empType)
      : [...currentTypes, empType];
    setFormData({
      ...formData,
      conditions: { ...formData.conditions, employee_types: newTypes }
    });
  };

  // Get all section keys for collapse/expand all
  const allSectionKeys = useMemo(() => {
    const keys = [];
    Object.entries(groupedRules).forEach(([ebaName, sections]) => {
      Object.keys(sections).forEach(sectionName => {
        keys.push(`${ebaName}:${sectionName}`);
      });
    });
    return keys;
  }, [groupedRules]);

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const expandAll = () => {
    const expanded = {};
    allSectionKeys.forEach(key => {
      expanded[key] = false;
    });
    setCollapsedSections(expanded);
  };

  const collapseAll = () => {
    const collapsed = {};
    allSectionKeys.forEach(key => {
      collapsed[key] = true;
    });
    setCollapsedSections(collapsed);
  };

  const handleBulkToggleSection = async (sectionRules, enable) => {
    const sectionKey = `${sectionRules[0]?.eba_type}:${sectionRules[0]?.source_section}`;
    setTogglingSection(sectionKey);
    
    try {
      // Update all rules in the section
      await Promise.all(
        sectionRules.map(rule => 
          API.put(`/rules/${rule.id}`, { is_active: enable })
        )
      );
      
      const action = enable ? 'enabled' : 'disabled';
      toast.success(`${sectionRules.length} rules ${action}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rules');
    } finally {
      setTogglingSection(null);
    }
  };

  const resetForm = () => {
    setFormData({
      eba_id: 'default',
      eba_type: 'default',
      category: '',
      conditions: { 
        day_type: 'weekday',
        rate_type: '',
        value: '',
        unit: 'per hour',
        role_ids: [],
        equipment_type_ids: [],
        employee_types: []
      },
      outputs: { 
        ordinary_hours: 8, 
        overtime_1_5: 2, 
        overtime_2_0: 0,
        rate_value: '',
        rate_unit: 'per hour'
      },
      explanation: '',
      source_section: '',
      source_hint: ''
    });
    setEditingRule(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setIsSheetOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    // Support both old format (role_id) and new format (role_ids)
    const roleIds = rule.conditions?.role_ids || (rule.conditions?.role_id ? [rule.conditions.role_id] : []);
    const equipIds = rule.conditions?.equipment_type_ids || (rule.conditions?.equipment_type_id ? [rule.conditions.equipment_type_id] : []);
    const empTypes = rule.conditions?.employee_types || [];
    
    setFormData({
      eba_id: rule.eba_id || 'default',
      eba_type: rule.eba_type || 'default',
      category: rule.category || '',
      conditions: {
        day_type: rule.conditions?.day_type || 'weekday',
        rate_type: rule.conditions?.rate_type || '',
        value: rule.conditions?.value || '',
        unit: rule.conditions?.unit || 'per hour',
        role_ids: roleIds,
        equipment_type_ids: equipIds,
        employee_types: empTypes
      },
      outputs: {
        ordinary_hours: rule.outputs?.ordinary_hours || 8,
        overtime_1_5: rule.outputs?.overtime_1_5 || 2,
        overtime_2_0: rule.outputs?.overtime_2_0 || 0,
        rate_value: rule.outputs?.rate_value || '',
        rate_unit: rule.outputs?.rate_unit || 'per hour'
      },
      explanation: rule.explanation || '',
      source_section: rule.source_section || '',
      source_hint: rule.source_hint || ''
    });
    setIsSheetOpen(true);
  };

  const handleDuplicate = (rule) => {
    // Don't set editingRule - this creates a new rule
    setEditingRule(null);
    // Support both old format (role_id) and new format (role_ids)
    const roleIds = rule.conditions?.role_ids || (rule.conditions?.role_id ? [rule.conditions.role_id] : []);
    const equipIds = rule.conditions?.equipment_type_ids || (rule.conditions?.equipment_type_id ? [rule.conditions.equipment_type_id] : []);
    const empTypes = rule.conditions?.employee_types || [];
    
    setFormData({
      eba_id: 'default',
      eba_type: 'default',
      category: `${rule.category} (Copy)`,
      conditions: {
        day_type: rule.conditions?.day_type || 'weekday',
        rate_type: rule.conditions?.rate_type || '',
        value: rule.conditions?.value || '',
        unit: rule.conditions?.unit || 'per hour',
        role_ids: roleIds,
        equipment_type_ids: equipIds,
        employee_types: empTypes
      },
      outputs: {
        ordinary_hours: rule.outputs?.ordinary_hours || 8,
        overtime_1_5: rule.outputs?.overtime_1_5 || 2,
        overtime_2_0: rule.outputs?.overtime_2_0 || 0,
        rate_value: rule.outputs?.rate_value || '',
        rate_unit: rule.outputs?.rate_unit || 'per hour'
      },
      explanation: rule.explanation || '',
      source_section: 'Custom Rules',
      source_hint: `Duplicated from: ${rule.category}`
    });
    setIsSheetOpen(true);
    toast.info('Rule duplicated - edit and save as new');
  };

  const handleSave = async () => {
    // Check if editing an EBA rule (partial edit mode)
    const isEditingEbaRule = editingRule && editingRule.eba_id && editingRule.eba_id !== 'default';
    
    if (!isEditingEbaRule) {
      // Full validation for non-EBA rules
      if (!formData.category || !formData.explanation) {
        toast.error('Please fill in Category and Explanation');
        return;
      }

      // Validate that value is provided for rate-based rules
      if (!formData.conditions.value && !formData.outputs.ordinary_hours) {
        toast.error('Please provide a rate value or hour outputs');
        return;
      }
    }

    setSaving(true);
    try {
      let savedRuleData = null;
      
      if (isEditingEbaRule) {
        // For EBA rules, only send role_ids, equipment_type_ids, and employee_types
        const partialPayload = {
          conditions: {
            role_ids: formData.conditions.role_ids || [],
            equipment_type_ids: formData.conditions.equipment_type_ids || [],
            employee_types: formData.conditions.employee_types || []
          }
        };
        await API.put(`/rules/${editingRule.id}`, partialPayload);
        savedRuleData = { 
          ...editingRule, 
          conditions: { ...editingRule.conditions, ...partialPayload.conditions }
        };
        toast.success('Assignments saved');
      } else if (editingRule) {
        // Full update for custom rules
        const payload = {
          category: formData.category,
          conditions: formData.conditions,
          outputs: formData.outputs,
          explanation: formData.explanation,
          source_section: formData.source_section || null,
          source_hint: formData.source_hint || null
        };
        await API.put(`/rules/${editingRule.id}`, payload);
        savedRuleData = { ...editingRule, ...payload };
        toast.success('Rule updated successfully');
      } else {
        // Create new rule
        const payload = {
          category: formData.category,
          conditions: formData.conditions,
          outputs: formData.outputs,
          explanation: formData.explanation,
          source_section: formData.source_section || null,
          source_hint: formData.source_hint || null,
          eba_id: formData.eba_id,
          eba_type: formData.eba_type
        };
        const response = await API.post('/rules', payload);
        savedRuleData = { ...payload, id: response.data?.id, rule_id: response.data?.rule_id };
        toast.success('Rule created successfully');
      }
      
      // Record rule pattern for AI learning (only if assignments were made)
      if (savedRuleData && (
        (formData.conditions.role_ids?.length > 0) ||
        (formData.conditions.equipment_type_ids?.length > 0) ||
        (formData.conditions.employee_types?.length > 0)
      )) {
        recordRulePattern(savedRuleData, isEditingEbaRule ? 'update_assignments' : (editingRule ? 'update_rule' : 'create_rule'));
      }
      
      setIsSheetOpen(false);
      setAiSuggestions(null);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await API.put(`/rules/${rule.id}`, { is_active: !rule.is_active });
      toast.success(`Rule ${rule.is_active ? 'deactivated' : 'activated'}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleDelete = async (ruleId) => {
    setDeletingRule(ruleId);
    try {
      await API.delete(`/rules/${ruleId}`);
      toast.success('Rule deleted successfully');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    } finally {
      setDeletingRule(null);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      const response = await API.delete('/rules');
      toast.success(response.data.message || 'All rules deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rules');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteEbaRules = async (ebaName, rules) => {
    setDeletingEba(ebaName);
    try {
      // Get the eba_id from the first rule in this group
      const firstRule = rules[0];
      if (firstRule?.eba_id && firstRule.eba_id !== 'default') {
        await API.delete(`/rules/by-eba/${firstRule.eba_id}`);
      } else {
        // For User Defined rules, delete by section
        await API.delete('/rules/by-section', {
          params: { eba_type: ebaName, source_section: 'Custom Rules' }
        });
      }
      toast.success(`Deleted all rules from "${ebaName}"`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rules');
    } finally {
      setDeletingEba(null);
    }
  };

  const handleDeleteSection = async (ebaName, sectionName, sectionRules) => {
    const sectionKey = `${ebaName}:${sectionName}`;
    setDeletingSection(sectionKey);
    try {
      await API.delete('/rules/by-section', {
        params: { eba_type: ebaName, source_section: sectionName }
      });
      toast.success(`Deleted ${sectionRules.length} rules from "${sectionName}"`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete section rules');
    } finally {
      setDeletingSection(null);
    }
  };

  return (
    <div className="p-6 h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-equipr-yellow">
              <Settings className="w-5 h-5 text-equipr-black" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Rules Engine</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Configure deterministic payroll rules from EBA interpretations</p>
        </div>
        <div className="flex items-center gap-3">
          {rules.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid="remove-all-rules-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove All Rules
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Remove All Rules?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all {rules.length} payroll rules. 
                    This action cannot be undone. You will need to recreate rules 
                    from your EBA documents or manually.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAll}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={deletingAll}
                    data-testid="confirm-remove-all-btn"
                  >
                    {deletingAll ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Yes, Remove All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={handleCreateNew} className="gap-2" data-testid="create-rule-btn">
            <Plus className="w-4 h-4" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Rules Table */}
      <Card className="border-slate-200 animate-fade-in-up-delay-1 mt-6 flex-1 flex flex-col min-h-0">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading font-semibold text-primary">
                Payroll Rules
              </CardTitle>
              <CardDescription>
                {(searchQuery || filterRuleType !== 'all' || filterPriority !== 'all' || filterStatus !== 'all')
                  ? `${filteredRulesCount} of ${rules.length} rule${rules.length !== 1 ? 's' : ''} shown`
                  : `${rules.length} rule${rules.length !== 1 ? 's' : ''} configured`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {/* Export for client validation */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportRules}
                disabled={exporting || filteredRulesCount === 0}
                className="h-8 px-3 text-xs gap-1.5"
                data-testid="export-rules-btn"
              >
                {exporting ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                Export for Validation
              </Button>
              {/* Collapse/Expand All Buttons */}
              {allSectionKeys.length > 0 && (
                <div className="flex items-center gap-1 border-r border-slate-200 pr-3">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={expandAll}
                    className="h-8 px-2 text-xs"
                    data-testid="expand-all-btn"
                  >
                    <ChevronsUpDown className="w-3.5 h-3.5 mr-1" />
                    Expand All
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={collapseAll}
                    className="h-8 px-2 text-xs"
                    data-testid="collapse-all-btn"
                  >
                    <ChevronRight className="w-3.5 h-3.5 mr-1" />
                    Collapse All
                  </Button>
                </div>
              )}
              
              {/* Filter by EBA */}
              {ebaDocuments.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Filter by EBA:</Label>
                  <Select value={filterEba} onValueChange={setFilterEba}>
                    <SelectTrigger className="w-[200px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Documents</SelectItem>
                      {ebaDocuments.map((eba) => (
                        <SelectItem key={eba.id} value={eba.id}>
                          {eba.filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Search + Filters toolbar */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search category, condition, action…"
                className="h-8 pl-8 text-sm"
                data-testid="rules-search-input"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <Select value={filterRuleType} onValueChange={setFilterRuleType}>
                <SelectTrigger className="w-[170px] h-8 text-sm" data-testid="rules-filter-type">
                  <SelectValue placeholder="Rule type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All rule types</SelectItem>
                  {ruleTypeOptions.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="rules-filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="rules-filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Enabled</SelectItem>
                <SelectItem value="inactive">Disabled</SelectItem>
              </SelectContent>
            </Select>
            {(searchQuery || filterRuleType !== 'all' || filterPriority !== 'all' || filterStatus !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSearchQuery(''); setFilterRuleType('all'); setFilterPriority('all'); setFilterStatus('all'); }}
                className="h-8 px-2 text-xs text-slate-500"
                data-testid="rules-clear-filters-btn"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8">
              <Settings className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No rules configured yet</p>
              <p className="text-sm text-slate-400">Create a rule or generate from an EBA document</p>
            </div>
          ) : filteredRulesCount === 0 ? (
            <div className="text-center py-8" data-testid="rules-no-match">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No rules match your filters</p>
              <p className="text-sm text-slate-400">Try adjusting the search or filters above</p>
            </div>
          ) : (
            <TooltipProvider>
              <ScrollArea className="flex-1 pr-4">
                {/* Group rules by parent category (source_section) */}
                {(() => {
                  let sectionNumber = 0;
                  const allEbaRules = Object.values(groupedRules).flatMap(sections => Object.values(sections).flat());
                  
                  return Object.entries(groupedRules).map(([ebaName, sections]) => {
                    const ebaRules = Object.values(sections).flat();
                    const isDeletingThisEba = deletingEba === ebaName;
                    
                    return (
                    <div key={ebaName} className="mb-8">
                      {/* EBA Document Header */}
                      <div className="flex items-center gap-3 mb-4 pb-2 border-b border-slate-200">
                        <FileText className="w-5 h-5 text-primary" />
                        <h3 className="font-heading font-semibold text-lg text-primary">{ebaName}</h3>
                        <Badge variant="secondary">
                          {ebaRules.length} rules
                        </Badge>
                        
                        {/* Delete EBA Rules Button */}
                        <div className="ml-auto">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
                                data-testid={`delete-eba-${ebaName}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Remove All
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                  <AlertTriangle className="w-5 h-5 text-red-500" />
                                  Remove All Rules from &quot;{ebaName}&quot;?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete all {ebaRules.length} rules from this source.
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteEbaRules(ebaName, ebaRules)}
                                  className="bg-red-600 hover:bg-red-700"
                                  disabled={isDeletingThisEba}
                                >
                                  {isDeletingThisEba ? (
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4 mr-2" />
                                  )}
                                  Yes, Remove All
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                      
                      {/* Sections within this EBA */}
                      <div className="space-y-4">
                        {Object.entries(sections).map(([sectionName, sectionRules]) => {
                          sectionNumber++;
                          const sectionKey = `${ebaName}:${sectionName}`;
                          const isCollapsed = collapsedSections[sectionKey];
                          const sectionDesc = sectionRules[0]?.source_hint || '';
                          const activeCount = sectionRules.filter(r => r.is_active).length;
                          const allActive = activeCount === sectionRules.length;
                          const allInactive = activeCount === 0;
                          const isToggling = togglingSection === sectionKey;
                          const isDeletingThisSection = deletingSection === sectionKey;
                          
                          return (
                            <Collapsible
                              key={sectionName}
                              open={!isCollapsed}
                              onOpenChange={() => toggleSection(sectionKey)}
                            >
                              <div className="border border-slate-200 rounded-lg overflow-hidden">
                                {/* Section Header - Clickable */}
                                <CollapsibleTrigger asChild>
                                  <div className="bg-equipr-yellow/10 border-b border-slate-200 p-4 cursor-pointer hover:bg-equipr-yellow/20 transition-colors">
                                    <div className="flex items-center gap-3">
                                      {/* Collapse Icon */}
                                      <div className="flex-shrink-0 text-slate-400">
                                        {isCollapsed ? (
                                          <ChevronRight className="w-5 h-5" />
                                        ) : (
                                          <ChevronDown className="w-5 h-5" />
                                        )}
                                      </div>
                                      
                                      {/* Section Number */}
                                      <span className="w-8 h-8 rounded-full bg-equipr-black text-equipr-yellow text-sm font-bold flex items-center justify-center flex-shrink-0">
                                        {sectionNumber}
                                      </span>
                                      
                                      {/* Section Title & Description */}
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-heading font-semibold text-equipr-black text-base">
                                          {sectionName}
                                        </h4>
                                        {sectionDesc && sectionDesc !== sectionName && (
                                          <p className="text-sm text-slate-600 mt-0.5 truncate">
                                            {sectionDesc}
                                          </p>
                                        )}
                                      </div>
                                      
                                      {/* Bulk Toggle & Delete Buttons */}
                                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant={allActive ? "default" : "outline"}
                                              size="sm"
                                              className="h-7 px-2 text-xs gap-1"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleBulkToggleSection(sectionRules, true);
                                              }}
                                              disabled={isToggling || allActive}
                                              data-testid={`enable-section-${sectionNumber}`}
                                            >
                                              {isToggling ? (
                                                <RefreshCw className="w-3 h-3 animate-spin" />
                                              ) : (
                                                <ToggleRight className="w-3 h-3" />
                                              )}
                                              Enable All
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Enable all {sectionRules.length} rules in this section
                                          </TooltipContent>
                                        </Tooltip>
                                        
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant={allInactive ? "secondary" : "outline"}
                                              size="sm"
                                              className="h-7 px-2 text-xs gap-1"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleBulkToggleSection(sectionRules, false);
                                              }}
                                              disabled={isToggling || allInactive}
                                              data-testid={`disable-section-${sectionNumber}`}
                                            >
                                              {isToggling ? (
                                                <RefreshCw className="w-3 h-3 animate-spin" />
                                              ) : (
                                                <ToggleLeft className="w-3 h-3" />
                                              )}
                                              Disable All
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Disable all {sectionRules.length} rules in this section
                                          </TooltipContent>
                                        </Tooltip>

                                        {/* Remove Section Rules Button */}
                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 px-2 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                              onClick={(e) => e.stopPropagation()}
                                              data-testid={`remove-section-${sectionNumber}`}
                                            >
                                              <Trash2 className="w-3 h-3" />
                                              Remove
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle className="flex items-center gap-2">
                                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                                Remove Section Rules?
                                              </AlertDialogTitle>
                                              <AlertDialogDescription>
                                                This will permanently delete all {sectionRules.length} rules in &quot;{sectionName}&quot;.
                                                This action cannot be undone.
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                                              <AlertDialogAction
                                                onClick={() => handleDeleteSection(ebaName, sectionName, sectionRules)}
                                                className="bg-red-600 hover:bg-red-700"
                                                disabled={isDeletingThisSection}
                                              >
                                                {isDeletingThisSection ? (
                                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                  <Trash2 className="w-4 h-4 mr-2" />
                                                )}
                                                Yes, Remove
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                      
                                      {/* Rate Count & Active Status */}
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">
                                          {activeCount}/{sectionRules.length} active
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                </CollapsibleTrigger>
                                
                                {/* Collapsible Rates Table */}
                                <CollapsibleContent>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Category</th>
                                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Rate Type</th>
                                      <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Value</th>
                                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Unit</th>
                                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Condition (IF)</th>
                                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Action (THEN)</th>
                                      <th className="text-center px-4 py-2.5 font-semibold text-slate-600">Priority</th>
                                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Effective</th>
                                      <th className="text-center px-4 py-2.5 font-semibold text-slate-600">Status</th>
                                      <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sectionRules.map((rule, ruleIdx) => (
                                      <tr 
                                        key={rule.id} 
                                        className={`border-b border-slate-100 hover:bg-slate-50 ${!rule.is_active ? 'opacity-50' : ''}`}
                                        data-testid={`rule-row-${rule.id}`}
                                      >
                                        <td className="px-4 py-2.5">
                                          <p className="font-medium text-slate-800">{rule.category}</p>
                                          <p className="text-xs text-slate-400 font-mono">{rule.rule_id}</p>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600">
                                          <div className="space-y-1">
                                            <span className="text-xs">{rule.conditions?.rate_type || '-'}</span>
                                            {rule.conditions?.rate_category && (
                                              <Badge 
                                                variant="outline" 
                                                className={`text-[10px] px-1.5 py-0 block w-fit ${
                                                  rule.conditions.rate_category === 'allowance' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                                  rule.conditions.rate_category === 'base_rate' ? 'bg-green-50 text-green-700 border-green-200' :
                                                  rule.conditions.rate_category === 'overtime' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                  rule.conditions.rate_category === 'loading' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                  'bg-slate-50 text-slate-700 border-slate-200'
                                                }`}
                                              >
                                                {rule.conditions.rate_category}
                                              </Badge>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                          <span className="font-mono font-semibold text-primary">
                                            {rule.conditions?.value || rule.outputs?.rate_value || '-'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600">
                                          {rule.conditions?.unit || rule.outputs?.rate_unit || '-'}
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-500 text-xs max-w-[220px]">
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="cursor-help space-y-1">
                                                <span className="line-clamp-2">
                                                  {rule.condition_text || rule.conditions?.condition_text || rule.explanation?.split(' - ')[1] || rule.conditions?.day_type || '-'}
                                                </span>
                                                {(() => {
                                                  // Support both old (single) and new (array) formats
                                                  const roleIds = rule.conditions?.role_ids || (rule.conditions?.role_id ? [rule.conditions.role_id] : []);
                                                  const equipIds = rule.conditions?.equipment_type_ids || (rule.conditions?.equipment_type_id ? [rule.conditions.equipment_type_id] : []);
                                                  const roleNames = getRoleNames(roleIds);
                                                  const equipNames = getEquipmentTypeNames(equipIds);
                                                  
                                                  if (roleNames.length === 0 && equipNames.length === 0) return null;
                                                  
                                                  return (
                                                    <div className="flex flex-wrap gap-1">
                                                      {roleNames.map((name, idx) => (
                                                        <Badge key={`role-${idx}`} variant="outline" className="text-[10px] px-1 py-0 bg-amber-50 border-amber-200">
                                                          <Users className="w-2.5 h-2.5 mr-0.5" />
                                                          {name}
                                                        </Badge>
                                                      ))}
                                                      {equipNames.map((name, idx) => (
                                                        <Badge key={`equip-${idx}`} variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 border-blue-200">
                                                          <Wrench className="w-2.5 h-2.5 mr-0.5" />
                                                          {name}
                                                        </Badge>
                                                      ))}
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="max-w-sm">
                                              <p className="text-sm">{rule.explanation}</p>
                                              {(() => {
                                                const roleIds = rule.conditions?.role_ids || (rule.conditions?.role_id ? [rule.conditions.role_id] : []);
                                                const equipIds = rule.conditions?.equipment_type_ids || (rule.conditions?.equipment_type_id ? [rule.conditions.equipment_type_id] : []);
                                                const roleNames = getRoleNames(roleIds);
                                                const equipNames = getEquipmentTypeNames(equipIds);
                                                return (
                                                  <>
                                                    {roleNames.length > 0 && (
                                                      <p className="text-xs text-amber-600 mt-1">Roles: {roleNames.join(', ')}</p>
                                                    )}
                                                    {equipNames.length > 0 && (
                                                      <p className="text-xs text-blue-600 mt-1">Equipment: {equipNames.join(', ')}</p>
                                                    )}
                                                  </>
                                                );
                                              })()}
                                            </TooltipContent>
                                          </Tooltip>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600 text-xs max-w-[220px]">
                                          <span className="line-clamp-2">
                                            {rule.action_text || rule.outputs?.action_text || '-'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                          {rule.priority ? (
                                            <Badge
                                              variant="outline"
                                              className={`text-[10px] px-1.5 py-0 ${
                                                String(rule.priority).toLowerCase() === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                                                String(rule.priority).toLowerCase() === 'low' ? 'bg-slate-50 text-slate-600 border-slate-200' :
                                                'bg-amber-50 text-amber-700 border-amber-200'
                                              }`}
                                            >
                                              {rule.priority}
                                            </Badge>
                                          ) : (
                                            <span className="text-slate-400 text-xs">-</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                                          {rule.effective_date || '-'}
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                          <Switch
                                            checked={rule.is_active}
                                            onCheckedChange={() => handleToggleActive(rule)}
                                            data-testid={`rule-toggle-${rule.id}`}
                                          />
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                          <div className="flex items-center justify-end gap-1">
                                            {/* Show Assign button for EBA rules, Edit for user-defined rules */}
                                            {rule.eba_type && rule.eba_type !== 'default' ? (
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 gap-1"
                                                    onClick={() => handleEdit(rule)}
                                                    data-testid={`assign-rule-btn-${rule.id}`}
                                                  >
                                                    <Users className="w-3.5 h-3.5" />
                                                    <span className="text-xs">Assign</span>
                                                  </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>Assign Roles & Equipment to this EBA rule</TooltipContent>
                                              </Tooltip>
                                            ) : (
                                              <>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-7 w-7 p-0"
                                                      onClick={() => handleEdit(rule)}
                                                      data-testid={`edit-rule-btn-${rule.id}`}
                                                    >
                                                      <Edit className="w-3.5 h-3.5" />
                                                    </Button>
                                                  </TooltipTrigger>
                                                  <TooltipContent>Edit rule</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-7 w-7 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                                                      onClick={() => handleDuplicate(rule)}
                                                      data-testid={`duplicate-rule-btn-${rule.id}`}
                                                    >
                                                      <Copy className="w-3.5 h-3.5" />
                                                    </Button>
                                                  </TooltipTrigger>
                                                  <TooltipContent>Duplicate rule</TooltipContent>
                                                </Tooltip>
                                              </>
                                            )}
                                            <AlertDialog>
                                              <AlertDialogTrigger asChild>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                  data-testid={`delete-rule-btn-${rule.id}`}
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                              </AlertDialogTrigger>
                                              <AlertDialogContent>
                                                <AlertDialogHeader>
                                                  <AlertDialogTitle className="flex items-center gap-2">
                                                    <AlertTriangle className="w-5 h-5 text-red-500" />
                                                    Delete Rule?
                                                  </AlertDialogTitle>
                                                  <AlertDialogDescription>
                                                    This will permanently delete the rule &quot;{rule.category}&quot;. 
                                                    This action cannot be undone.
                                                  </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                  <AlertDialogAction
                                                    onClick={() => handleDelete(rule.id)}
                                                    className="bg-red-600 hover:bg-red-700"
                                                    disabled={deletingRule === rule.id}
                                                    data-testid={`confirm-delete-btn-${rule.id}`}
                                                  >
                                                    {deletingRule === rule.id ? (
                                                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                      <Trash2 className="w-4 h-4 mr-2" />
                                                    )}
                                                    Yes, Delete
                                                  </AlertDialogAction>
                                                </AlertDialogFooter>
                                              </AlertDialogContent>
                                            </AlertDialog>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                                </CollapsibleContent>
                              </div>
                            </Collapsible>
                          );
                        })}
                      </div>
                    </div>
                  );
                  });
                })()}
              </ScrollArea>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editingRule ? (isEbaRule ? 'Assign Roles & Equipment' : 'Edit Rule') : 'Create New Rule'}
            </SheetTitle>
            <SheetDescription>
              {isEbaRule 
                ? 'This rule was imported from an EBA document. You can only assign Roles, Equipment Types, and Employee Types.'
                : 'Configure the conditions and outputs for this payroll rule'
              }
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-200px)] pr-4 mt-6">
            <div className="space-y-6">
              {/* EBA Rule Notice */}
              {isEbaRule && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">EBA Document Rule</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Source: {editingRule.source_section || 'Unknown Section'}
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Rate values and conditions are locked to preserve EBA accuracy.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* EBA Rule - Read-only Summary */}
              {isEbaRule && (
                <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/50">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Rule Details (Read-only)</Label>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Category:</span>
                      <span className="font-medium">{editingRule.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Rate Type:</span>
                      <span className="font-medium">{editingRule.conditions?.rate_type || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Value:</span>
                      <span className="font-medium">
                        {editingRule.outputs?.rate_value || editingRule.conditions?.value || '-'} 
                        {editingRule.outputs?.rate_unit || editingRule.conditions?.unit || ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Day Type:</span>
                      <span className="font-medium capitalize">{editingRule.conditions?.day_type || 'any'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Category - Only for non-EBA rules */}
              {!isEbaRule && (
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Input
                    placeholder="e.g., Tower Crane Operator CW7-120%"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="rule-category-input"
                  />
                </div>
              )}

              {/* Rate Information Section - Only for non-EBA rules */}
              {!isEbaRule && (
                <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50/50">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Rate Information
                </Label>
                
                <div className="space-y-2">
                  <Label className="text-sm">Rate Type</Label>
                  <Select
                    value={formData.conditions.rate_type || ''}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, rate_type: value }
                    })}
                  >
                    <SelectTrigger data-testid="rule-rate-type-select">
                      <SelectValue placeholder="Select rate type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Base Hourly Rate">Base Hourly Rate</SelectItem>
                      <SelectItem value="Base Weekly Rate">Base Weekly Rate</SelectItem>
                      <SelectItem value="Overtime Rate">Overtime Rate</SelectItem>
                      <SelectItem value="Penalty Rate">Penalty Rate</SelectItem>
                      <SelectItem value="Allowance">Allowance</SelectItem>
                      <SelectItem value="Loading">Loading</SelectItem>
                      <SelectItem value="Site Allowance">Site Allowance</SelectItem>
                      <SelectItem value="Multi-Storey Allowance">Multi-Storey Allowance</SelectItem>
                      <SelectItem value="Meal Allowance">Meal Allowance</SelectItem>
                      <SelectItem value="Travel Allowance">Travel Allowance</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Value *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 68.39"
                      value={formData.conditions.value}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          conditions: { ...formData.conditions, value: val },
                          outputs: { ...formData.outputs, rate_value: val }
                        });
                      }}
                      data-testid="rule-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Unit</Label>
                    <Select
                      value={formData.conditions.unit || 'per hour'}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        conditions: { ...formData.conditions, unit: value },
                        outputs: { ...formData.outputs, rate_unit: value }
                      })}
                    >
                      <SelectTrigger data-testid="rule-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per hour">per hour</SelectItem>
                        <SelectItem value="per week">per week</SelectItem>
                        <SelectItem value="per day">per day</SelectItem>
                        <SelectItem value="per shift">per shift</SelectItem>
                        <SelectItem value="flat rate">flat rate</SelectItem>
                        <SelectItem value="x base">x base (multiplier)</SelectItem>
                        <SelectItem value="per storey">per storey</SelectItem>
                        <SelectItem value="percentage">percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                </div>
              )}

              {/* Conditions Section - Only for non-EBA rules */}
              {!isEbaRule && (
                <div className="space-y-2">
                  <Label>Day Type Condition</Label>
                  <Select
                    value={formData.conditions.day_type}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, day_type: value }
                    })}
                  >
                    <SelectTrigger data-testid="rule-day-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekday">Weekday</SelectItem>
                      <SelectItem value="saturday">Saturday</SelectItem>
                      <SelectItem value="sunday">Sunday</SelectItem>
                      <SelectItem value="public_holiday">Public Holiday</SelectItem>
                      <SelectItem value="any">Any Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Role & Equipment Type Conditions */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-amber-50/30">
                <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Users className="w-3.5 h-3.5" />
                  Role & Equipment Conditions (Optional)
                </Label>
                <p className="text-xs text-slate-500 -mt-2">
                  Select one or more roles and/or equipment types this rule applies to
                </p>
                
                {/* AI Suggestions Panel */}
                {(aiSuggestions || loadingAiSuggestions) && (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">AI Suggestions</span>
                      {loadingAiSuggestions && (
                        <RefreshCw className="w-3 h-3 text-purple-500 animate-spin" />
                      )}
                    </div>
                    
                    {aiSuggestions?.ai_disabled && (
                      <p className="text-xs text-slate-600">
                        AI suggestions are disabled. Enable in <span className="font-medium">Settings → AI</span> tab.
                      </p>
                    )}
                    
                    {aiSuggestions?.has_suggestions === false && !aiSuggestions?.ai_disabled && (
                      <p className="text-xs text-slate-600">
                        {aiSuggestions?.message || 'No suggestions available yet. Save rules with assignments to train AI.'}
                      </p>
                    )}
                    
                    {aiSuggestions?.has_suggestions && (
                      <div className="space-y-2">
                        {/* Suggested Roles */}
                        {aiSuggestions.suggested_roles?.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-[10px] text-slate-500 mr-1">Roles:</span>
                            {aiSuggestions.suggested_roles.slice(0, 5).map((s, i) => (
                              <Badge 
                                key={i}
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-amber-50 border-amber-300 cursor-pointer hover:bg-amber-100 transition-colors"
                                onClick={() => applyAiSuggestion('roles', [s])}
                              >
                                <Sparkles className="w-2.5 h-2.5 mr-0.5 text-amber-600" />
                                {s.name} ({s.confidence}%)
                              </Badge>
                            ))}
                            {aiSuggestions.suggested_roles.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1.5 text-[10px] text-purple-600 hover:text-purple-700"
                                onClick={() => applyAiSuggestion('roles', aiSuggestions.suggested_roles)}
                              >
                                Add All
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {/* Suggested Equipment */}
                        {aiSuggestions.suggested_equipment?.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-[10px] text-slate-500 mr-1">Equipment:</span>
                            {aiSuggestions.suggested_equipment.slice(0, 5).map((s, i) => (
                              <Badge 
                                key={i}
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-blue-50 border-blue-300 cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() => applyAiSuggestion('equipment', [s])}
                              >
                                <Sparkles className="w-2.5 h-2.5 mr-0.5 text-blue-600" />
                                {s.name} ({s.confidence}%)
                              </Badge>
                            ))}
                            {aiSuggestions.suggested_equipment.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1.5 text-[10px] text-purple-600 hover:text-purple-700"
                                onClick={() => applyAiSuggestion('equipment', aiSuggestions.suggested_equipment)}
                              >
                                Add All
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {/* Suggested Employee Types */}
                        {aiSuggestions.suggested_employee_types?.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-[10px] text-slate-500 mr-1">Employee Types:</span>
                            {aiSuggestions.suggested_employee_types.slice(0, 5).map((s, i) => (
                              <Badge 
                                key={i}
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-green-50 border-green-300 cursor-pointer hover:bg-green-100 transition-colors"
                                onClick={() => applyAiSuggestion('employee_types', [s])}
                              >
                                <Sparkles className="w-2.5 h-2.5 mr-0.5 text-green-600" />
                                {s.name} ({s.confidence}%)
                              </Badge>
                            ))}
                            {aiSuggestions.suggested_employee_types.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1.5 text-[10px] text-purple-600 hover:text-purple-700"
                                onClick={() => applyAiSuggestion('employee_types', aiSuggestions.suggested_employee_types)}
                              >
                                Add All
                              </Button>
                            )}
                          </div>
                        )}
                        
                        <p className="text-[10px] text-slate-400 mt-1">
                          Based on {aiSuggestions.matching_patterns || aiSuggestions.patterns_analyzed} similar rule configurations. Click to apply.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Roles Multi-Select */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Roles
                      {formData.conditions.role_ids?.length > 0 && (
                        <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
                          {formData.conditions.role_ids.length} selected
                        </Badge>
                      )}
                    </Label>
                    <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white space-y-1">
                      {roles.length === 0 ? (
                        <p className="text-xs text-slate-400 italic p-1">No roles defined</p>
                      ) : (
                        roles.map((role) => (
                          <div 
                            key={role.id} 
                            className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer"
                            onClick={() => toggleRoleSelection(role.id)}
                          >
                            <Checkbox
                              id={`role-${role.id}`}
                              checked={formData.conditions.role_ids?.includes(role.id) || false}
                              onCheckedChange={() => toggleRoleSelection(role.id)}
                            />
                            <label 
                              htmlFor={`role-${role.id}`} 
                              className="text-sm cursor-pointer flex-1"
                            >
                              {role.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    {formData.conditions.role_ids?.length === 0 && (
                      <p className="text-[10px] text-slate-400">Leave empty to apply to all roles</p>
                    )}
                  </div>
                  
                  {/* Equipment Types Multi-Select */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Wrench className="w-3 h-3" />
                      Equipment Types
                      {formData.conditions.equipment_type_ids?.length > 0 && (
                        <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
                          {formData.conditions.equipment_type_ids.length} selected
                        </Badge>
                      )}
                    </Label>
                    <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white space-y-1">
                      {equipmentTypes.length === 0 ? (
                        <p className="text-xs text-slate-400 italic p-1">No equipment types defined</p>
                      ) : (
                        equipmentTypes.map((equip) => (
                          <div 
                            key={equip.id} 
                            className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer"
                            onClick={() => toggleEquipmentSelection(equip.id)}
                          >
                            <Checkbox
                              id={`equip-${equip.id}`}
                              checked={formData.conditions.equipment_type_ids?.includes(equip.id) || false}
                              onCheckedChange={() => toggleEquipmentSelection(equip.id)}
                            />
                            <label 
                              htmlFor={`equip-${equip.id}`} 
                              className="text-sm cursor-pointer flex-1"
                            >
                              {equip.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    {formData.conditions.equipment_type_ids?.length === 0 && (
                      <p className="text-[10px] text-slate-400">Leave empty to apply to all equipment</p>
                    )}
                  </div>
                </div>

                {/* Employee Types Multi-Select */}
                <div className="space-y-2 mt-4">
                  <Label className="text-sm flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Employee Types
                    {formData.conditions.employee_types?.length > 0 && (
                      <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
                        {formData.conditions.employee_types.length} selected
                      </Badge>
                    )}
                  </Label>
                  <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white space-y-1">
                    {employeeTypes.length === 0 ? (
                      <p className="text-xs text-slate-400 italic p-1">Loading employee types...</p>
                    ) : (
                      employeeTypes.map((empType) => (
                        <div 
                          key={empType} 
                          className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer"
                          onClick={() => toggleEmployeeTypeSelection(empType)}
                        >
                          <Checkbox
                            id={`emp-type-${empType}`}
                            checked={formData.conditions.employee_types?.includes(empType) || false}
                            onCheckedChange={() => toggleEmployeeTypeSelection(empType)}
                          />
                          <label 
                            htmlFor={`emp-type-${empType}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {empType}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {formData.conditions.employee_types?.length === 0 && (
                    <p className="text-[10px] text-slate-400">Leave empty to apply to all employee types</p>
                  )}
                </div>
              </div>

              {/* Hour Outputs Section */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Hour Allocation (for time-based rules)
                </Label>
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Ordinary</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.outputs.ordinary_hours}
                      onChange={(e) => setFormData({
                        ...formData,
                        outputs: { ...formData.outputs, ordinary_hours: parseFloat(e.target.value) || 0 }
                      })}
                      data-testid="rule-ordinary-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">OT 1.5x</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.outputs.overtime_1_5}
                      onChange={(e) => setFormData({
                        ...formData,
                        outputs: { ...formData.outputs, overtime_1_5: parseFloat(e.target.value) || 0 }
                      })}
                      data-testid="rule-ot15-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">OT 2x</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.outputs.overtime_2_0}
                      onChange={(e) => setFormData({
                        ...formData,
                        outputs: { ...formData.outputs, overtime_2_0: parseFloat(e.target.value) || 0 }
                      })}
                      data-testid="rule-ot20-input"
                    />
                  </div>
                </div>
              </div>

              {/* Source Information Section */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-blue-50/30">
                <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />
                  Source & Audit Trail (Optional)
                </Label>
                
                <div className="space-y-2">
                  <Label className="text-sm">Source Section</Label>
                  <Input
                    placeholder="e.g., Base Wage Rates – Tower Crane"
                    value={formData.source_section}
                    onChange={(e) => setFormData({ ...formData, source_section: e.target.value })}
                    data-testid="rule-source-section-input"
                  />
                  <p className="text-xs text-slate-400">Used for grouping rules in the display</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Source Hint / Reference</Label>
                  <Input
                    placeholder="e.g., Appendix B, Clause 23.4"
                    value={formData.source_hint}
                    onChange={(e) => setFormData({ ...formData, source_hint: e.target.value })}
                    data-testid="rule-source-hint-input"
                  />
                  <p className="text-xs text-slate-400">Reference to EBA document location</p>
                </div>
              </div>

              {/* Explanation - Only for non-EBA rules */}
              {!isEbaRule && (
                <div className="space-y-2">
                  <Label>Explanation / Conditions *</Label>
                  <Textarea
                    placeholder="Describe when this rule applies and any conditions..."
                    value={formData.explanation}
                    onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                    rows={3}
                    data-testid="rule-explanation-input"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  className="flex-1"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-rule-btn"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isEbaRule ? 'Save Assignments' : (editingRule ? 'Update Rule' : 'Create Rule')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsSheetOpen(false);
                    resetForm();
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default RulesPage;
